import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Divider,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { GoogleLogin } from "@react-oauth/google";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import eyeSlash from "../../../assets/icons/eye-slash.svg";
import eye from "../../../assets/icons/eye.svg";
import { googleLoginAction, loginAction } from "../Actions/CommonActions";
import { goToMentionedRoutes, LoadingSlides } from "../CommonComponents/Common";
import ErrorPopUp from "../CommonComponents/PopUpCards/ErrorPopUp";
import { checkValidationAccountAction } from "../Store/Account/Account.action";
import { commonStyles } from "../../../assets/MUIStyles/commonStyles";
import {
  getDecryptedItem,
  setEncryptedItem,
} from "../../../utils/localStorageHelper";
import { axiosClient } from "../../../Constant/apiConstant";
import { ReactComponent as ConfirmationIcon } from "../../../assets/icons/Confirmation.svg";

const Login = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const reducer = useSelector((reducer: any) => reducer);
  const {
    AccountReducer: { publicKey },
  } = reducer;
  const [loginData, setLoginData] = useState({
    email: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [spin, setSpin] = useState(false);
  const [errorObj, setErrorObj] = useState({
    show: false,
    errMsg: "",
  });
  const [warningObj, setWarningObj] = useState({
    show: false,
    warningMsg: "",
  });
  const { email, password } = loginData;

  useEffect(() => {
    const init = async () => {
      const token = getDecryptedItem("token");
      if (token) {
        setSpin(true);

        const res: any = await dispatch(
          checkValidationAccountAction({ token: `Bearer ${token}` })
        );

        if (res?.errMsg === "authorized") {
          const { accounts, lastLogin, _id } = res?.data;
          const find =
            accounts?.find((acc) => acc?._id === lastLogin?.accountId) ||
            accounts?.[0];
          if (find && !find.accountName) {
            setEncryptedItem("account-id", find?._id);
            axiosClient.defaults.headers.common["dbUrl"] = find?.accountUrl;
            history.push(`/account-name`);
          } else {
            await goToMentionedRoutes(find, _id, history, dispatch);
          }
        }
      }
      setSpin(false);
    };

    if (publicKey) init();
  }, [publicKey]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setLoginData((prev) => ({ ...prev, [name]: value }));
  };

  const handleTogglePassword = () => {
    setShowPassword((prevState) => !prevState);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setSpin(true);
    if (!email || !password) {
      let warningMsg = "";
      if (!email && !password) {
        warningMsg = "Email and Password are required.";
      } else if (!email) {
        warningMsg = "Email is required.";
      } else if (!password) {
        warningMsg = "Password is required.";
      }

      setWarningObj({
        show: true,
        warningMsg,
      });
      setSpin(false);
      return;
    }
    try {
      const obj = {
        email: email.trim(),
        password: password,
      };
      const res: any = await dispatch(loginAction(obj));
      if (!res?.token) {
        setErrorObj({
          show: true,
          errMsg: res?.errMsg,
        });
        setSpin(false);
        return;
      }

      if (res) {
        const { accounts, lastLogin, _id } = res;
        let find =
          accounts?.find((acc) => acc?._id === lastLogin?.accountId) ||
          accounts[0];
        if (find && !find.accountName) {
          setEncryptedItem("account-id", find?._id);
          axiosClient.defaults.headers.common["dbUrl"] = find?.accountUrl;
          history.push(`/account-name`);
        } else {
          await goToMentionedRoutes(find, _id, history, dispatch);
        }
      }
    } catch (error) {
      setErrorObj({
        show: true,
        errMsg: "Email or Password is incorrect.",
      });
    }
    setSpin(false);
  };

  const googleResponse = async (response: any) => {
    if (response?.credential) {
      const obj = {
        token: response.credential,
      };
      const res: any = await dispatch(googleLoginAction(obj));
      if (res) {
        const { accounts, lastLogin, _id } = res;
        const find =
          accounts?.find((acc) => acc?._id === lastLogin?.accountId) ||
          accounts?.[0];
        if (find && !find.accountName) {
          setEncryptedItem("account-id", find?._id);
          axiosClient.defaults.headers.common["dbUrl"] = find?.accountUrl;
          history.push(`/account-name`);
        } else {
          await goToMentionedRoutes(find, _id, history, dispatch);
        }
      }
    }
  };

  const onFailure: any = (error) => {
    if (error?.err) {
      setErrorObj({
        show: true,
        errMsg: error?.err,
      });
    }
  };

  return (
    <Box className="main-container">
      <Typography className="font-14-grey">Welcome back!</Typography>
      <Typography className="font-28-700-grey mb-2">Login</Typography>
      <Box className="google-login">
        <GoogleLogin
          onSuccess={googleResponse}
          onError={onFailure}
          useOneTap
          theme="outline"
          text="signin_with"
          shape="rectangular"
          logo_alignment="center"
          width="100%"
        />
      </Box>
      <Box className="my-2 flex-align-center">
        <Divider sx={{ flexGrow: 1 }} />
        <Typography>or</Typography>
        <Divider sx={{ flexGrow: 1 }} />
      </Box>
      <form onSubmit={handleLogin}>
        <Box className="mb-2">
          <Typography className="font-14-grey, mb-1">Email</Typography>
          <TextField
            name="email"
            value={email?.toLowerCase()}
            onChange={handleChange}
            variant="outlined"
            fullWidth
            placeholder="Enter your registered email"
            sx={commonStyles.inputField}
          />
        </Box>
        <Box className="mb-2">
          <Typography className="font-14-grey, mb-1">Password</Typography>
          <TextField
            name="password"
            value={password}
            onChange={handleChange}
            type={showPassword ? "text" : "password"}
            variant="outlined"
            fullWidth
            placeholder="Enter your password"
            sx={commonStyles.inputField}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleTogglePassword}
                      edge="end"
                      sx={{ padding: "0", marginRight: 1.5 }}
                      disableRipple
                      disableFocusRipple
                      style={{ outline: "none" }}
                    >
                      <img
                        src={showPassword ? eyeSlash : eye}
                        alt="password visibility"
                      />
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
          />
          <Box component={"span"} className="flex-end mt-3">
            <Typography
              onClick={() => history.push("/reset-password")}
              component={"span"}
              sx={commonStyles.forgotPasswordLink}
            >
              Forgot Password?
            </Typography>{" "}
          </Box>
        </Box>
        <Button
          type="submit"
          disabled={spin}
          variant="contained"
          sx={commonStyles.loginButton}
          fullWidth
        >
          {spin ? <LoadingSlides bgColor="white" /> : "Login"}
        </Button>
      </form>
      <Typography className="font-14-400-grey text-center mt-3 ">
        Don’t have an account?{" "}
        <Box
          component={"span"}
          onClick={() => history.push("/register")}
          sx={commonStyles.signupLink}
        >
          Sign-up
        </Box>
      </Typography>
      {errorObj.show && (
        <ErrorPopUp
          show={errorObj.show}
          toggle={() =>
            setErrorObj((prev) => ({
              ...prev,
              show: false,
              errMsg: "",
            }))
          }
          heading={"Error"}
          message={errorObj?.errMsg}
          spin={spin}
        />
      )}
      {warningObj.show && (
        <ErrorPopUp
          show={warningObj.show}
          toggle={() =>
            setWarningObj((prev) => ({
              ...prev,
              show: false,
              warningMsg: "",
            }))
          }
          heading={"Warning"}
          headerText="Warning"
          message={warningObj?.warningMsg}
          Icon={ConfirmationIcon}
        />
      )}
    </Box>
  );
};

export default Login;
